import { mutateTransportation } from "@/api/transportation/useTransportationQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormSelect } from "@/features/standard/form/FormSelect";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { VehicleAutoComplete } from "@/features/ui/autoComplete/vehicle/vehicle-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Select, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  ScmApiTransportationPostRequest,
  TransportationGet200ResponseRowsInnerDirectionEnum,
  TransportationGetRequestStatusEnum,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface TransportationCreateFormProps {
  orderId?: number;
  name?: string;
  partnerCode?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}

export const TransportationCreateForm = (props: TransportationCreateFormProps) => {
  const { partnerCode, autoCompleteOnSubmit } = props;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutate: createMutate } = useMutation(
    (params: ScmApiTransportationPostRequest) =>
      mutateTransportation
        .create(params)
        .mutationFn(params as ScmApiTransportationPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["transportation"]);
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", { item: t("transportation.transportation") }),
          t("common.createSuccess", t("transportation.transportation")),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("transportation.transportation") }),
          t("common.createFail", { item: t("transportation.transportation") }),
          "red",
        );
      },
    },
  );

  const form = useForm({
    initialValues: {
      partnerCode: partnerCode ?? undefined,
      direction: undefined,
      status: TransportationGetRequestStatusEnum.SCHEDULED,
      scheduledAt: undefined,
      additional: undefined,
      vehicleCode: undefined,
    },
  });

  const onSubmit = async () => {
    try {
      createMutate({
        transportationGetRequest: {
          partnerCode: form.values.partnerCode!,
          direction: form.values.direction!,
          status: form.values.status!,
          scheduledAt: form.values.scheduledAt!,
          additional: form.values.additional!,
          vehicleCode: form.values.vehicleCode!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("transportation.transportation") }),
        t("common.createFail", { item: t("transportation.transportation") }),
        "red",
      );
    }
  };

  return (
    <Create.Wrapper>
      <Select
        withAsterisk
        label={t("transportation.direction")}
        placeholder={t("placeholder.select", { item: t("transportation.direction") })}
        data={[
          {
            label: t(TransportationGet200ResponseRowsInnerDirectionEnum.INBOUND),
            value: "INBOUND",
          },
          {
            label: t(TransportationGet200ResponseRowsInnerDirectionEnum.OUTBOUND),
            value: "OUTBOUND",
          },
        ]}
        {...form.getInputProps("direction")}
      />
      <Create.DateInput
        popoverProps={{ withinPortal: true }}
        icon={<IconCalendar size="1.2rem" />}
        label={t("stockPlan.scheduledAt")}
        valueFormat="YYYY-MM-DD"
        locale="ko"
        firstDayOfWeek={0}
        {...form.getInputProps("scheduledAt")}
      />
      <PartnerAutoComplete
        label={t("partner.partner")}
        placeholder={t("placeholder.select", { item: t("partner.partner") })}
        {...form.getInputProps("partnerCode")}
        withAsterisk
      />
      <VehicleAutoComplete
        label={t("vehicle.code")}
        placeholder={t("placeholder.select", { item: t("vehicle.code") })}
        {...form.getInputProps("vehicleCode")}
        withAsterisk
      />
      <Create.Input
        label={t("transportation.description")}
        {...form.getInputProps("description")}
      />
      <Create.ButtonBox>
        <Create.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Create.Button>
        <Create.Button
          disabled={validateFormValues({
            pageType: "transportation",
            values: form.values,
          })}
          onClick={autoCompleteOnSubmit ? () => autoCompleteOnSubmit(form.values) : onSubmit}
        >
          {t("common.additional", { item: t("transportation.transportation") })}
        </Create.Button>
      </Create.ButtonBox>
    </Create.Wrapper>
  );
};
